<script>
	// 블록의 "콘텐츠(contents)" 편집기 — 블록 카드와 코스 화면(평가)에서 **같은 것**을 쓴다.
	//
	// 왜 뺐나: 평가도 블록이라 이미지·고정 문구·카드를 구성할 수 있어야 하는데, 그 UI가
	//   BlockContext의 블록 카드 안에 700줄로 박혀 있어 코스 화면에서 쓸 수가 없었다.
	//   자리를 빌려 뷰를 전환하는 우회로를 썼다가, 결국 이 컴포넌트로 뺐다.
	//
	// 계약(부모가 지는 책임)
	//   · block      — contents를 직접 변형한다(Svelte bind 대신 이벤트로 알린다)
	//   · schemas    — 서버 blockSchemas(getBlockSchemas). 타입 목록·필드 정의·라벨의 출처
	//   · assetMap   — assetId → 자산(미리보기 URL resolve용)
	//   · firstSlotNote — 첫 AI 텍스트 칸이 무슨 역할인지 한 줄 안내(HTML 아님, 없으면 안 붙는다).
	//                     스텝이면 "여는 말", 평가면 "평가문 지침" — 같은 슬롯인데 화면마다 뜻이 다르다.
	//   · on:change  — 무엇이든 바뀌었을 때(부모가 markDirty)
	//   · on:pickImage — 이미지 고르기 요청 {content, key}. 모달은 부모가 소유한다
	import { createEventDispatcher } from "svelte";
	import SchemaForm from "./SchemaForm.svelte";
	import { autoGrow } from "./autoGrow";
	import { Select } from "../../lib/ui";
	import { PRConnect } from "@projectroom/ui-lib";
	import { createAsset } from "./imageAssetApi";

	export let block;
	export let schemas = {};
	export let assetMap = {};
	export let firstSlotNote = "";
	export let chatId = "";   // 지도 이미지를 자산으로 올릴 때 필요

	let mapFetchingId = null; // 지도 이미지를 받아오는 중인 content(객체 동일성으로 식별)

	const dispatch = createEventDispatcher();
	const change = () => dispatch("change");
	const pickImage = (content, key) => dispatch("pickImage", { content, key });

	$: schemaTypes = Object.values(schemas);
	const base64ToFile = (b64, mime, name) => {
		const bin = atob(b64);
		const arr = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
		return new File([arr], name, { type: mime || "image/png" });
	};
	const assetUrl = (assetId, cacheUrl) => (assetId && assetMap[assetId]?.url) || cacheUrl || "";
	/** 이미지 자산 해제 — assetId를 떼고 캐시 URL 자리도 비운다 */
	const clearImageAsset = (content, key) => {
		const { assetId, ...rest } = content.data;
		content.data = { ...rest, [key]: "" };
		change();
	};

	// text 슬롯 모드: 'ai'(2차 LLM이 채움) / 'static'(고정 문구). AI 슬롯은 블록당 1개 제한.
	const isAiText = (c) =>
		c.type === "text" && (c.data?.mode ?? "ai") !== "static";
	const hasAiText = (block, exceptIdx = -1) =>
		block.contents.some((c, i) => i !== exceptIdx && isAiText(c));
	const setTextMode = (block, ci, mode) => {
		if (mode === "ai" && hasAiText(block, ci)) {
			alert(
				"AI 생성 텍스트는 블록당 1개만 가능합니다.\n기존 AI 텍스트를 '직접 입력'으로 바꾼 뒤 시도하세요.",
			);
			return;
		}
		block.contents[ci].data = { ...block.contents[ci].data, mode };
		change();
	};
	// 콘텐츠 추가/삭제/순서변경
	const addContent = (block, type) => {
		if (!type) return;
		// 폼은 블록당 1개(제출이 이 블록으로 매핑되므로 2개면 모호). 이미 있으면 무시.
		if (type === "form" && block.contents.some((c) => c.type === "form")) return;
		// 새 텍스트는 기본 'ai', 단 이미 AI 슬롯이 있으면 'static'으로 (블록당 AI 1개)
		const data =
			type === "text"
				? { mode: hasAiText(block) ? "static" : "ai" }
				: type === "form"
					? { fields: [], completionMessage: "", submitLabel: "제출", cancelLabel: "취소" }
					: type === "map"
						? { provider: "kakao", name: "", address: "" }
						: {};
		block.contents = [...block.contents, { type, data }];
		change();
	};
	const removeContent = (block, i) => {
		block.contents = block.contents.filter((_, idx) => idx !== i);
		change();
	};
	const moveContent = (block, i, dir) => {
		const j = i + dir;
		if (j < 0 || j >= block.contents.length) return;
		const arr = [...block.contents];
		[arr[i], arr[j]] = [arr[j], arr[i]];
		block.contents = arr;
		change();
	};
	const addFormField = (block, content) => {
		content.data.fields = [...(content.data.fields || []), { key: "", ask: "", type: "text", required: true }];
		change();
	};
	const removeFormField = (block, content, i) => {
		content.data.fields = (content.data.fields || []).filter((_, idx) => idx !== i);
		change();
	};
	const moveFormField = (block, content, i, dir) => {
		const arr = content.data.fields || [];
		const j = i + dir;
		if (j < 0 || j >= arr.length) return;
		[arr[i], arr[j]] = [arr[j], arr[i]];
		content.data.fields = arr;
		change();
	};
	const labelOf = (type) => schemas[type]?.label ?? type;
	// 스텝을 열 때 나가는 말 = 첫 AI 텍스트 콘텐츠(서버 BlockAssembler가 그 슬롯을 채워 렌더).
	// 그 칸에만 "여는 말"이라는 안내를 붙인다(나머지 AI 텍스트는 평범한 슬롯).
	const firstAiTextIndex = (block) => (block?.contents || []).findIndex(isAiText);
	// select 타입 필드의 선택지(options) 편집
	const addFieldOption = (field) => {
		field.options = [...(field.options || []), ""];
		change();
	};
	const removeFieldOption = (field, i) => {
		field.options = (field.options || []).filter((_, idx) => idx !== i);
		change();
	};
	const fetchMapForContent = (content) => {
		const address = (content.data.address || "").trim();
		const lat = String(content.data.lat ?? "").trim();
		const lng = String(content.data.lng ?? "").trim();
		if (!address && !(lat && lng)) {
			alert("주소 또는 좌표(위도/경도)를 입력해 주세요.");
			return;
		}
		mapFetchingId = content;
		change();
		PRConnect.send("/api/Chat/fetchMapImage", { address, lat, lng }, async (data) => {
			try {
				if (data.header.status !== "success") {
					alert(data.header.message || "지도 이미지를 가져오지 못했어요.");
					return;
				}
				const { lat, lng, base64, mimeType } = data.result || {};
				const ext = (mimeType || "").includes("png") ? "png" : "jpg";
				const file = base64ToFile(base64, mimeType, `map-${Date.now()}.${ext}`);
				const asset = await createAsset(chatId, file, "maps");
				content.data.assetId = asset.id;
				content.data.imageUrl = asset.url;
				content.data.lat = lat;
				content.data.lng = lng;
				change();
			} catch (e) {
				alert("지도 이미지를 저장하지 못했어요: " + (e?.message || e));
			} finally {
				mapFetchingId = null;
				change();
			}
		});
	};
	const fetchOg = (content) => {
		const url = (content.data?.url || "").trim();
		if (!url) return;
		PRConnect.send("/api/Chat/getLinkPreview", { url }, (data) => {
			if (data.header.status === "success" && data.result) {
				const r = data.result;
				content.data = {
					...content.data,
					url: r.url || url,
					title: r.title || content.data.title || "",
					body: r.body || content.data.body || "",
					imageUrl: r.imageUrl || content.data.imageUrl || "",
				};
				change();
			}
		});
	};
	const moveGalleryImage = (content, i, dir) => {
		const images = [...(content.data.images ?? [])];
		const j = i + dir;
		if (j < 0 || j >= images.length) return;
		[images[i], images[j]] = [images[j], images[i]];
		content.data = { ...content.data, images };
		change();
	};
	const removeGalleryImage = (content, i) => {
		content.data = {
			...content.data,
			images: (content.data.images ?? []).filter((_, idx) => idx !== i),
		};
		change();
	};
	// 이미지 표시 방식: zoom(눌러서 크게, 기본) / fill(말풍선 폭 채우기 — 원래 비율, 확대 없음).
	const setImageVariant = (content, variant) => {
		content.data = { ...content.data, variant };
		change();
	};
	// 캐러셀 표시 방식: single(한 장씩) / multi(≈3장) / peek(양옆 걸치기) / grid(격자). 아이콘으로 선택.
	const setGalleryVariant = (content, variant) => {
		content.data = { ...content.data, variant };
		change();
	};

	/** 갤러리 이미지 여러 장 고르기 — 모달은 부모가 소유한다 */
	const openGalleryPicker = (content) => dispatch("pickGallery", { content });
</script>

				<!-- 콘텐츠(스키마 자동생성 폼) -->
				<div class="block-sub">
					<div class="tab-text sub2">콘텐츠</div>
					{#each block.contents as content, ci}
						<div class="content-item">
							<div class="content-head">
								<span class="content-type"
									>{labelOf(content.type)}</span
								>
								<span class="spacer" />
								<button
									type="button"
									class="mini"
									title="위로"
									on:click={() => moveContent(block, ci, -1)}
									disabled={ci === 0}>↑</button
								>
								<button
									type="button"
									class="mini"
									title="아래로"
									on:click={() => moveContent(block, ci, 1)}
									disabled={ci === block.contents.length - 1}
									>↓</button
								>
								<button
									type="button"
									class="mini del"
									on:click={() => removeContent(block, ci)}
									>삭제</button
								>
							</div>
							{#if content.type === "text"}
								<div class="text-mode">
									<button
										type="button"
										class="mode-btn"
										class:active={isAiText(content)}
										on:click={() =>
											setTextMode(block, ci, "ai")}
										>AI 생성</button
									>
									<button
										type="button"
										class="mode-btn"
										class:active={!isAiText(content)}
										on:click={() =>
											setTextMode(block, ci, "static")}
										>직접 입력</button
									>
								</div>
								{#if isAiText(content)}
									<!-- 콘텐츠 슬롯 = "이 블록 턴에 이 자리를 채울 말"(한 턴짜리). 스텝이면 그게 곧 여는 말이다.
									     대화 내내 따를 내용은 콘텐츠가 아니라 아래 [이 스텝의 상황](completion.situation). -->
									{#if firstSlotNote && ci === firstAiTextIndex(block)}
										<div class="field-note">{firstSlotNote}</div>
									{/if}
									<textarea
										class="text-field textarea"
										rows="3"
										placeholder="이 자리에 무슨 말을 채울지 (LLM 지침)"
										bind:value={content.data.prompt}
										use:autoGrow={content.data.prompt}
										on:input={() => change()}
									></textarea>
								{:else}
									<textarea
										class="text-field textarea"
										rows="3"
										placeholder="화면에 그대로 표시할 문구"
										bind:value={content.data.text}
										use:autoGrow={content.data.text}
										on:input={() => change()}
									></textarea>
								{/if}
							{:else if content.type === "image"}
								<div
									class="tab-text sub"
									style="margin-bottom:6px;"
								>
									표시 방식
								</div>
								<div class="variant-select">
									<button
										type="button"
										class="variant-btn"
										class:active={(content.data.variant ??
											"zoom") !== "fill"}
										title="16:9 미리보기 — 누르면 크게 보여줍니다"
										on:click={() =>
											setImageVariant(content, "zoom")}
									>
										<svg
											class="v-ic"
											viewBox="0 0 28 18"
											fill="currentColor"
											><rect
												x="3"
												y="3"
												width="15"
												height="11"
												rx="1.5"
											/><circle
												cx="21.5"
												cy="11"
												r="3"
												fill="none"
												stroke="currentColor"
												stroke-width="1.5"
											/><line
												x1="23.7"
												y1="13.2"
												x2="26"
												y2="15.5"
												stroke="currentColor"
												stroke-width="1.5"
												stroke-linecap="round"
											/></svg
										>
										<span>눌러서 크게</span>
									</button>
									<button
										type="button"
										class="variant-btn"
										class:active={content.data.variant ===
											"fill"}
										title="말풍선 폭에 꽉 채웁니다 — 세로로 긴 그림은 4:5까지만 보이고(가운데 기준) 눌러도 커지지 않습니다"
										on:click={() =>
											setImageVariant(content, "fill")}
									>
										<svg
											class="v-ic"
											viewBox="0 0 28 18"
											fill="currentColor"
											><rect
												x="1"
												y="2"
												width="26"
												height="14"
												rx="3"
											/></svg
										>
										<span>말풍선 채우기</span>
									</button>
								</div>
								<div
									class="tab-text sub"
									style="margin-bottom:6px;"
								>
									이미지
								</div>
								{#if content.data.assetId || content.data.url}
									<div class="asset-current">
										<img
											src={assetUrl(
												content.data.assetId,
												content.data.url,
											)}
											alt=""
											loading="lazy"
										/>
										<button
											type="button"
											class="pick-btn"
											on:click={() =>
												pickImage(content, "url")}>변경</button
										>
										<button
											type="button"
											class="mini del"
											on:click={() =>
												clearImageAsset(content, "url")}>제거</button
										>
									</div>
								{:else}
									<button
										type="button"
										class="pick-empty"
										on:click={() =>
											pickImage(content, "url")}>＋ 이미지 선택</button
									>
								{/if}
								<input
									class="text-field"
									type="text"
									placeholder="설명(alt)"
									bind:value={content.data.alt}
									on:input={() => change()}
									style="margin-top:6px;"
								/>
							{:else if content.type === "gallery"}
								<div
									class="tab-text sub"
									style="margin-bottom:6px;"
								>
									표시 방식
								</div>
								<div class="variant-select">
									<button
										type="button"
										class="variant-btn"
										class:active={(content.data.variant ??
											"single") === "single"}
										title="한 장씩"
										on:click={() =>
											setGalleryVariant(content,
												"single",
											)}
									>
										<svg
											class="v-ic"
											viewBox="0 0 28 18"
											fill="currentColor"
											><rect
												x="4"
												y="3"
												width="20"
												height="12"
												rx="2"
											/></svg
										>
										<span>한 장씩</span>
									</button>
									<button
										type="button"
										class="variant-btn"
										class:active={(content.data.variant ??
											"single") === "multi"}
										title="여러 장"
										on:click={() =>
											setGalleryVariant(content,
												"multi",
											)}
									>
										<svg
											class="v-ic"
											viewBox="0 0 28 18"
											fill="currentColor"
											><rect
												x="2"
												y="4"
												width="7"
												height="10"
												rx="1.5"
											/><rect
												x="10.5"
												y="4"
												width="7"
												height="10"
												rx="1.5"
											/><rect
												x="19"
												y="4"
												width="7"
												height="10"
												rx="1.5"
											/></svg
										>
										<span>여러 장</span>
									</button>
									<button
										type="button"
										class="variant-btn"
										class:active={(content.data.variant ??
											"single") === "peek"}
										title="살짝 걸치기"
										on:click={() =>
											setGalleryVariant(content,
												"peek",
											)}
									>
										<svg
											class="v-ic"
											viewBox="0 0 28 18"
											fill="currentColor"
											><rect
												x="1"
												y="5"
												width="4"
												height="8"
												rx="1"
												opacity="0.4"
											/><rect
												x="8"
												y="3"
												width="12"
												height="12"
												rx="2"
											/><rect
												x="23"
												y="5"
												width="4"
												height="8"
												rx="1"
												opacity="0.4"
											/></svg
										>
										<span>살짝 걸치기</span>
									</button>
									<button
										type="button"
										class="variant-btn"
										class:active={(content.data.variant ??
											"single") === "grid"}
										title="격자"
										on:click={() =>
											setGalleryVariant(content,
												"grid",
											)}
									>
										<svg
											class="v-ic"
											viewBox="0 0 28 18"
											fill="currentColor"
											><rect
												x="4"
												y="2"
												width="9"
												height="6.5"
												rx="1.5"
											/><rect
												x="15"
												y="2"
												width="9"
												height="6.5"
												rx="1.5"
											/><rect
												x="4"
												y="9.5"
												width="9"
												height="6.5"
												rx="1.5"
											/><rect
												x="15"
												y="9.5"
												width="9"
												height="6.5"
												rx="1.5"
											/></svg
										>
										<span>격자</span>
									</button>
								</div>

								{#if (content.data.images ?? []).length}
									<div
										class="tab-text sub"
										style="margin:10px 0 6px;"
									>
										이미지 관리
									</div>
									<div class="gallery-list">
										{#each content.data.images as img, gi}
											<div class="gallery-item">
												<img
													class="gallery-thumb"
													src={assetUrl(
														img.assetId,
														img.url,
													)}
													alt=""
													loading="lazy"
												/>
												<input
													class="text-field"
													type="text"
													placeholder="설명(alt)"
													bind:value={img.alt}
													on:input={() =>
														change()}
												/>
												<div
													class="gallery-item-actions"
												>
													<button
														type="button"
														class="mini"
														title="앞으로"
														on:click={() =>
															moveGalleryImage(content,
																gi,
																-1,
															)}
														disabled={gi === 0}
														>↑</button
													>
													<button
														type="button"
														class="mini"
														title="뒤로"
														on:click={() =>
															moveGalleryImage(content,
																gi,
																1,
															)}
														disabled={gi ===
															content.data.images
																.length -
																1}>↓</button
													>
													<button
														type="button"
														class="mini del"
														on:click={() =>
															removeGalleryImage(content,
																gi,
															)}>삭제</button
													>
												</div>
											</div>
										{/each}
									</div>
								{/if}
								<button
									type="button"
									class="pick-empty"
									style="margin-top:8px;"
									on:click={() =>
										openGalleryPicker(content)}
									>＋ 라이브러리에서 이미지 추가</button
								>
							{:else if content.type === "card"}
								<div class="og-row">
									<input
										class="text-field"
										type="url"
										placeholder="링크 URL (선택) → 가져오기로 OG 자동 채움"
										bind:value={content.data.url}
										on:input={() => change()}
									/>
									<button
										type="button"
										class="mini add"
										on:click={() => fetchOg(content)}
										>가져오기</button
									>
								</div>
								<SchemaForm
									fields={(
										schemas[content.type]?.fields ?? []
									).filter((f) => f.key !== "imageUrl")}
									data={content.data}
									onChange={() => change()}
								/>
								<div
									class="tab-text sub"
									style="margin-top:6px;margin-bottom:6px;"
								>
									이미지
								</div>
								{#if content.data.assetId || content.data.imageUrl}
									<div class="asset-current">
										<img
											src={assetUrl(
												content.data.assetId,
												content.data.imageUrl,
											)}
											alt=""
											loading="lazy"
										/>
										<button
											type="button"
											class="pick-btn"
											on:click={() =>
												pickImage(content, "imageUrl")}>변경</button
										>
										<button
											type="button"
											class="mini del"
											on:click={() =>
												clearImageAsset(content, "imageUrl")}>제거</button
										>
									</div>
								{:else}
									<button
										type="button"
										class="pick-empty"
										on:click={() =>
											pickImage(content, "imageUrl")}>＋ 이미지 선택</button
									>
								{/if}
							{:else if content.type === "map"}
								<div class="tab-text sub" style="margin-bottom:6px;">지도 (오시는 길)</div>
								<input
									class="text-field"
									type="text"
									placeholder="장소명 (예: 그룹종합병원)"
									bind:value={content.data.name}
									on:input={() => change()}
								/>
								<input
									class="text-field"
									type="text"
									placeholder="주소 (검색·주소 복사에 사용)"
									bind:value={content.data.address}
									on:input={() => change()}
									style="margin-top:6px;"
								/>
								<div class="block-row inline" style="margin-top:6px;">
									<Select
										class="tw-flex-none tw-max-w-[96px]"
										bind:value={content.data.provider}
										on:change={() => change()}
									>
										<option value="kakao">카카오맵</option>
										<option value="naver">네이버맵</option>
										<option value="google">구글맵</option>
									</Select>
									<input
										class="text-field"
										type="text"
										placeholder="위도 lat (선택)"
										bind:value={content.data.lat}
										on:input={() => change()}
									/>
									<input
										class="text-field"
										type="text"
										placeholder="경도 lng (선택)"
										bind:value={content.data.lng}
										on:input={() => change()}
									/>
								</div>
								<div class="tab-text sub map-hint">
									주소 또는 좌표를 넣고 [지도 이미지 가져오기]. 좌표가 있으면 그 위치로 가져옵니다.
								</div>
								<div class="map-img-head">
									<span class="tab-text sub">프리뷰 이미지</span>
									<button
										type="button"
										class="mini add"
										on:click={() => fetchMapForContent(content)}
										disabled={mapFetchingId === content}
										title="좌표(위도/경도)가 있으면 그 좌표로, 없으면 주소로 네이버 지도 이미지를 가져옵니다"
										>{mapFetchingId === content
											? "가져오는 중…"
											: "지도 이미지 가져오기"}</button
									>
									<span class="map-img-or">또는 직접 업로드</span>
								</div>
								{#if content.data.assetId || content.data.imageUrl}
									<div class="asset-current">
										<img
											src={assetUrl(content.data.assetId, content.data.imageUrl)}
											alt=""
											loading="lazy"
										/>
										<button
											type="button"
											class="pick-btn"
											on:click={() => pickImage(content, "imageUrl")}
											>변경</button
										>
										<button
											type="button"
											class="mini del"
											on:click={() => clearImageAsset(content, "imageUrl")}
											>제거</button
										>
									</div>
								{:else}
									<button
										type="button"
										class="pick-empty"
										on:click={() => pickImage(content, "imageUrl")}
										>＋ 지도 이미지 선택</button
									>
								{/if}
							{:else if content.type === "form"}
								<div class="form-editor">
									<div class="tab-text sub form-cap">
										완료 멘트
										<span class="form-hint"
											>제출 후 보여줄 문구 (비우면 기본 안내)</span
										>
									</div>
									<textarea
										class="text-field textarea"
										rows="2"
										placeholder="예) 접수되었습니다. 확인 후 연락드릴게요."
										bind:value={content.data.completionMessage}
										use:autoGrow={content.data.completionMessage}
										on:input={() => change()}
									></textarea>

									<div class="tab-text sub form-cap">입력 필드</div>
									{#each content.data.fields ?? [] as field, fi}
										<div class="form-field">
											<div class="block-row inline">
												<input
													class="text-field"
													placeholder="내부 키 (예: 날짜)"
													bind:value={field.key}
													on:input={() => change()}
												/>
												<Select
													class="tw-flex-none tw-max-w-[96px]"
													bind:value={field.type}
													on:change={() => change()}
												>
													{#each FIELD_TYPES as t}
														<option value={t.value}
															>{t.label}</option
														>
													{/each}
												</Select>
												<button
													type="button"
													class="mini"
													title="위로"
													on:click={() =>
														moveFormField(block, content, fi, -1)}
													disabled={fi === 0}>↑</button
												>
												<button
													type="button"
													class="mini"
													title="아래로"
													on:click={() =>
														moveFormField(block, content, fi, 1)}
													disabled={fi ===
														(content.data.fields?.length ?? 0) - 1}
													>↓</button
												>
												<button
													type="button"
													class="mini del"
													on:click={() =>
														removeFormField(block, content, fi)}
													>삭제</button
												>
											</div>
											<input
												class="text-field"
												placeholder="질문 라벨 (예: 희망 날짜를 선택해 주세요)"
												bind:value={field.ask}
												on:input={() => change()}
											/>
											{#if field.type === "select"}
												<div class="form-options">
													<div class="form-hint">선택지</div>
													{#each field.options ?? [] as _opt, oi}
														<div class="block-row inline">
															<input
																class="text-field"
																placeholder={`선택지 ${oi + 1}`}
																bind:value={field.options[oi]}
																on:input={() => change()}
															/>
															<button
																type="button"
																class="mini del"
																on:click={() =>
																	removeFieldOption(field, oi)}
																>삭제</button
															>
														</div>
													{/each}
													<button
														type="button"
														class="mini add"
														on:click={() =>
															addFieldOption(field)}
														>+ 선택지</button
													>
												</div>
											{/if}
											<label class="form-required">
												<input
													type="checkbox"
													bind:checked={field.required}
													on:change={() => change()}
												/>
												필수 입력
											</label>
										</div>
									{/each}
									<button
										type="button"
										class="mini add"
										on:click={() => addFormField(block, content)}
										>+ 필드 추가</button
									>

									<div class="form-labels">
										<label class="form-label-item">
											<span class="form-hint">제출 버튼</span>
											<input
												class="text-field"
												placeholder="제출"
												bind:value={content.data.submitLabel}
												on:input={() => change()}
											/>
										</label>
										<label class="form-label-item">
											<span class="form-hint">취소 버튼</span>
											<input
												class="text-field"
												placeholder="취소"
												bind:value={content.data.cancelLabel}
												on:input={() => change()}
											/>
										</label>
									</div>
								</div>
							{:else}
								<SchemaForm
									fields={schemas[content.type]?.fields ?? []}
									data={content.data}
									onChange={() => change()}
								/>
							{/if}
						</div>
					{/each}
					<div class="block-add-zone">
						<div class="block-add-label">콘텐츠 추가</div>
						<div class="block-add-row">
							{#each schemaTypes as s}
								<button
									type="button"
									class="block-add-btn"
									on:click={() => addContent(block, s.type)}
									>+ {s.label}</button
								>
							{/each}
						</div>
					</div>
				</div>

<style>
	/* 이 편집기의 스타일 — BlockContext에서 함께 옮겨왔다.
	   Svelte는 스타일을 컴포넌트 단위로 스코프하므로, 마크업만 옮기면 부모의 규칙이 안 먹는다.
	   · 아래 앞부분: 이 편집기에서만 쓰이는 규칙(부모에서 제거)
	   · 뒷부분: 부모와 양쪽에서 쓰는 공용 규격(.text-field/.mini 등) — 양쪽에 같은 값이 있어야 한다 */
.field-note {
		font-size: 11px;
		line-height: 1.55;
		color: hsl(var(--sb-muted-foreground));
		margin-bottom: 4px;
	}
.content-item {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 14px;
		margin-bottom: 12px;
		background: hsl(var(--sb-muted) / 0.4);
	}
.content-head {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-bottom: 10px;
	}
.content-type {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-primary));
	}
.text-mode {
		display: flex;
		gap: 6px;
		margin-bottom: 6px;
	}
.mode-btn {
		padding: 3px 10px;
		font-size: 12px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 999px;
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-muted-foreground));
	}
.mode-btn.active {
		background: hsl(var(--sb-primary));
		border-color: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}
.form-editor {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
.form-cap {
		display: flex;
		align-items: baseline;
		gap: 6px;
		margin-top: 2px;
	}
.form-hint {
		font-size: 11px;
		font-weight: 400;
		color: hsl(var(--sb-muted-foreground));
	}
.form-field {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 6px;
		background: hsl(var(--sb-card));
	}
.form-options {
		display: flex;
		flex-direction: column;
		gap: 4px;
		padding-left: 8px;
		border-left: 2px solid hsl(var(--sb-border));
	}
.form-required {
		display: flex;
		align-items: center;
		gap: 5px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
.form-labels {
		display: flex;
		gap: 8px;
		margin-top: 4px;
	}
.form-label-item {
		display: flex;
		flex-direction: column;
		gap: 3px;
		flex: 1;
	}
.map-hint {
		margin-top: 4px;
		line-height: 1.5;
	}
.map-img-head {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
		margin: 10px 0 6px;
	}
.map-img-or {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}
.og-row {
		display: flex;
		gap: 6px;
		margin-bottom: 6px;
	}
.asset-current {
		display: flex;
		align-items: center;
		gap: 8px;
		margin: 4px 0;
	}
.asset-current img {
		width: 64px;
		height: 40px;
		object-fit: cover;
		border-radius: 6px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-muted));
	}
.pick-btn {
		padding: 5px 12px;
		border: 1px solid hsl(var(--sb-primary));
		border-radius: 7px;
		background: hsl(var(--sb-primary) / 0.1);
		color: hsl(var(--sb-primary));
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
	}
.pick-empty {
		width: 100%;
		padding: 12px;
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.4);
		color: hsl(var(--sb-primary));
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
	}
.pick-empty:hover {
		border-color: hsl(var(--sb-primary));
		background: hsl(var(--sb-primary) / 0.06);
	}
.variant-select {
		display: flex;
		gap: 6px;
		margin-bottom: 8px;
	}
.variant-btn {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 5px;
		padding: 8px 4px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-muted-foreground));
		font-size: 11px;
		line-height: 1;
	}
.variant-btn:hover {
		border-color: hsl(var(--sb-border));
	}
.variant-btn.active {
		border-color: hsl(var(--sb-primary));
		background: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}
.variant-btn .v-ic {
		width: 28px;
		height: 18px;
	}
.gallery-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
		margin-bottom: 4px;
	}
.gallery-item {
		display: flex;
		align-items: center;
		gap: 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 6px;
		background: hsl(var(--sb-card));
	}
.gallery-thumb {
		width: 48px;
		height: 48px;
		flex: none;
		object-fit: cover;
		border-radius: 6px;
		border: 1px solid hsl(var(--sb-border));
	}
.gallery-item-actions {
		display: flex;
		gap: 4px;
		flex: none;
	}
.block-add-zone {
		margin-top: 14px;
		padding: 12px;
		border: 1px solid hsl(var(--sb-primary) / 0.5);
		border-radius: 10px;
		background: hsl(var(--sb-muted) / 0.4);
	}
.block-add-label {
		font-size: 11px;
		font-weight: 600;
		color: hsl(var(--sb-primary));
		margin-bottom: 8px;
		padding-left: 2px;
	}
.block-add-row {
		display: flex;
		gap: 6px;
	}
.block-add-btn {
		flex: 1 1 0;
		min-width: 0;
		padding: 9px 6px;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
		border: 1px solid hsl(var(--sb-primary) / 0.55);
		border-radius: 8px;
		background: hsl(var(--sb-primary) / 0.18);
		color: hsl(var(--sb-primary));
	}
.block-add-btn:hover {
		background: hsl(var(--sb-primary) / 0.3);
		border-color: hsl(var(--sb-primary));
	}

	/* ── 부모와 공유하는 표면 규격(값이 갈리면 두 화면이 달라 보인다) ── */
.tab-text {
		color: hsl(var(--sb-foreground));
		font-size: 20px;
		font-weight: 700;
	}
.tab-text.sub2 {
		font-size: 12px;
		padding-left: 4px;
		margin-bottom: 10px;
		display: flex;
		align-items: center;
		gap: 8px;
	}
.tab-text.sub {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
.tab-text.sub.field-hint {
		font-weight: 400;
		padding-left: 4px;
	}
.text-field {
		width: 100%;
		padding: 8px 12px;
		border: 1px solid hsl(var(--sb-input));
		border-radius: calc(var(--sb-radius) - 2px);
		font-size: 14px;
		line-height: 1.5;
		font-family: inherit;
		color: hsl(var(--sb-foreground));
		background: hsl(var(--sb-background));
		box-sizing: border-box;
		transition:
			border-color 0.15s ease,
			box-shadow 0.15s ease;
	}
input.text-field {
		height: 40px;
	}
.text-field::placeholder {
		color: hsl(var(--sb-muted-foreground));
	}
.text-field:focus {
		outline: none;
		border-color: hsl(var(--sb-ring));
		box-shadow: 0 0 0 2px hsl(var(--sb-ring) / 0.35);
	}
.text-field:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
.block-row {
		margin-bottom: 14px;
		display: flex;
		flex-direction: column;
		gap: 7px;
	}
.block-row.inline {
		flex-direction: row;
		align-items: center;
		gap: 8px;
	}
.btn-row input.text-field {
		flex: 1 1 90px;
		min-width: 90px;
	}
.block-sub {
		margin: 20px 0;
		padding-top: 18px;
		border-top: 1px dashed hsl(var(--sb-border));
	}
.content-head .spacer {
		flex: 1;
	}
.text-field.textarea {
		resize: vertical;
		min-height: 60px;
	}
.gallery-item .text-field {
		flex: 1;
	}
button.mini {
		padding: 4px 10px;
		font-size: 12px;
		font-weight: 500;
		border: 1px solid hsl(var(--sb-border));
		border-radius: calc(var(--sb-radius) - 4px);
		background: hsl(var(--sb-secondary));
		color: hsl(var(--sb-secondary-foreground));
		transition: background-color 0.15s ease;
	}
button.mini:hover:not(:disabled) {
		background: hsl(var(--sb-accent));
	}
button.mini.add {
		background: transparent;
		border-style: dashed;
		color: hsl(var(--sb-primary));
	}
button.mini.add:hover:not(:disabled) {
		background: hsl(var(--sb-primary) / 0.08);
	}
button.mini.del {
		background: transparent;
		border-color: transparent;
		color: hsl(var(--sb-destructive));
	}
button.mini.del:hover:not(:disabled) {
		background: hsl(var(--sb-destructive) / 0.12);
	}
button.del {
		background: hsl(var(--sb-destructive) / 0.15);
		color: hsl(var(--sb-destructive));
	}
</style>
